import React, { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleProp, StyleSheet, View, ViewStyle } from 'react-native';
import Animated, { Extrapolation, interpolate, useAnimatedStyle } from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { Button } from '@/components/ButtonComponent';
import { SizedBox } from '@/components/SizedBox';
import { Text } from '@/components/TextComponent';
import { Box, Row } from '@/components/lib/components';
import { useBetslipData } from '@/feature/betslip-pickem/hooks/use-betslip-data';
import { useBetslipNavigation } from '@/feature/betslip-pickem/hooks/use-betslip-navigation';
import { getWinningPicksAndToWin } from '@/feature/betslip-pickem/utils/betslip-utils';
import { useFantasyFullScreenPagerTransition } from '@/feature/circular-pager/FantasyFullScreenPagerTransitionProvider';
import { getMultiplierBackgroundColor } from '@/feature/lobby/utils/multiplierUtils';
import { MaxWidthWrapper } from '@/feature/responsive-design/WebComponents';
import { common, designSystem } from '@/styles/styles';
import { getNonRegularProjections } from '@/utils/getProjectionsTypes';

import { PicksMultiplierProgressBar } from './PicksMultiplierProgressBar';

const SIZE = 24;
export const PICK_BAR_HEIGHT = 72;
const styles = StyleSheet.create({
    root: {
        backgroundColor: designSystem.colors.gray8,
        height: PICK_BAR_HEIGHT,
        paddingHorizontal: 16,
        paddingTop: 16,
        paddingBottom: 12,
        borderTopWidth: 1,
        borderTopColor: designSystem.colors.neutral800,
    },
    leftLabel: {
        color: designSystem.colors.gray2,
    },
    multiplierRoot: {
        flex: 1,
        paddingRight: 8,
    },
    multiplierLabel: {
        color: designSystem.colors.white,
    },
    picksNumber: {
        borderRadius: 100,
        backgroundColor: designSystem.colors.gray8,
        width: SIZE,
        height: SIZE,
        justifyContent: 'center',
        alignItems: 'center',
    },
    absolutedPositionedPickbar: {
        backgroundColor: designSystem.colors.gray8,
    },
});

const ANIMATED_PICK_BAR_INPUT_RANGE = [-1, -0.7]; // end at -0.7 to make the pick bar annimation faster

export const PickBar = ({ style }: { style?: StyleProp<ViewStyle> }) => {
    const { betslip: picks, validPicks } = useBetslipData();

    return (
        <MaxWidthWrapper>
            <View style={[styles.root, style]}>
                <View style={[common.row, common.flex]}>
                    {validPicks ? <Multiplier /> : <LeftLabel />}
                    <SizedBox value={24} />
                    <ViewEntry count={picks.length} />
                </View>
            </View>
        </MaxWidthWrapper>
    );
};

export const AnimatedPickBar = memo(() => {
    const { animatedIndex, sheetMountAnimationProgress, isSheetMountCompleted } = useFantasyFullScreenPagerTransition();
    const { bottom } = useSafeAreaInsets();
    const animatedPickBarHeight = useMemo(() => PICK_BAR_HEIGHT + bottom, [bottom]);

    const animatedStyle = useAnimatedStyle(() => {
        if (!isSheetMountCompleted.value) {
            return {
                transform: [
                    {
                        translateY: interpolate(
                            sheetMountAnimationProgress.value,
                            ANIMATED_PICK_BAR_INPUT_RANGE,
                            [animatedPickBarHeight, 0],
                            Extrapolation.CLAMP
                        ),
                    },
                ],
            };
        }

        return {
            transform: [
                {
                    translateY: interpolate(
                        animatedIndex.value,
                        ANIMATED_PICK_BAR_INPUT_RANGE,
                        [animatedPickBarHeight, 0],
                        Extrapolation.CLAMP
                    ),
                },
            ],
        };
    });

    return (
        <Box zIndex={1} position={'absolute'} bottom={0} left={0} right={0}>
            <Animated.View style={[animatedStyle, styles.absolutedPositionedPickbar]}>
                <PickBar style={{ marginBottom: bottom }} />
            </Animated.View>
        </Box>
    );
});

const LeftLabel = () => {
    const { t } = useTranslation('betslip_pickem');
    return (
        <View style={[common.flex, common.full, common.justifyCenter]}>
            <Text style={styles.leftLabel} variant="bodySmall">
                {t('min_entry_requirements')}
            </Text>
        </View>
    );
};

const Multiplier = () => {
    const { betslip, dynamicMultipliers, dynamicBoostedMultiplier, perfectActiveMultiplier } = useBetslipData();
    const { highestMultiplier } = getWinningPicksAndToWin(dynamicMultipliers.DYNAMIC, dynamicBoostedMultiplier);
    const { t } = useTranslation(['betslip_pickem']);
    const maxMultiplier = perfectActiveMultiplier ?? highestMultiplier;

    const hasValidMultiplier = maxMultiplier > 0;

    const { hasBoostedProjections } = getNonRegularProjections(betslip.map(pick => pick.projection));
    const backgroundColor = getMultiplierBackgroundColor(hasBoostedProjections, hasValidMultiplier);

    return (
        <Box flex={1} pr={'s8'} testID={'multiplier-bar'}>
            <Row mb={'s10'} alignItems={'center'}>
                {hasValidMultiplier ? (
                    <>
                        <Text variant="labelLarge">Multiplier: </Text>
                        <Text>{`${maxMultiplier}x`}</Text>
                    </>
                ) : (
                    <Text variant="labelLarge">{t('invalid_multiplier')}</Text>
                )}
            </Row>
            <PicksMultiplierProgressBar
                hasCustomPicks={hasBoostedProjections}
                multiplierBackgroundColor={backgroundColor}
            />
        </Box>
    );
};

const ViewEntry: React.FC<{ count: number }> = ({ count }) => {
    const { navigateToPickslip } = useBetslipNavigation();
    const { t } = useTranslation('betslip_pickem');

    return (
        <View style={[common.justifyCenter, common.alignEnd]}>
            <Button
                hierarchy="primary"
                shape="pill"
                analyticsTag={'view_lineup'}
                onPress={() => navigateToPickslip()}
                label={
                    <View style={common.row} accessible={false}>
                        <Text variant={'buttonLabel'} color={'gray8'}>
                            {t('view_lineup')}
                        </Text>
                        <SizedBox value={12} />
                        <View style={styles.picksNumber}>
                            <Text variant={'buttonLabel'} lineHeight={SIZE} testID="entrySize">
                                {count}
                            </Text>
                        </View>
                    </View>
                }
                accessible={false}
            />
        </View>
    );
};
